import React, { useState, useEffect } from 'react';
import { collectionsAPI, loansAPI } from '../services/api';
import './AgentPages.css';

const CollectionRecording = () => {
  const [formData, setFormData] = useState({
    retailerId: '',
    paymentMode: 'cash',
    referenceNumber: '',
    notes: ''
  });
  const [retailers, setRetailers] = useState([]);
  const [selectedRetailer, setSelectedRetailer] = useState('');
  const [loans, setLoans] = useState([]);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [collections, setCollections] = useState([]);
  const [currentDue, setCurrentDue] = useState(0);

  useEffect(() => {
    fetchRetailers();
    fetchTodayCollections();
  }, []);

  const fetchRetailers = async () => {
    try {
      setFetchLoading(true);
      setError(null);
      // Replace with your retailers API call
      const response = await import('../services/api').then(m => m.retailersAPI.list());
      let retailersData = [];
      if (Array.isArray(response)) {
        retailersData = response;
      } else if (response && response.data) {
        retailersData = response.data;
      } else if (response && response.retailers) {
        retailersData = response.retailers;
      }
      setRetailers(retailersData);
    } catch (err) {
      setError('Failed to load retailers: ' + (err.message || 'Network error'));
      setRetailers([]);
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchLoansForRetailer = async (retailerId) => {
    try {
      setFetchLoading(true);
      setError(null);
      const response = await loansAPI.list({ retailerId, status: 'ACTIVE' });
      let loansData = [];
      if (Array.isArray(response)) {
        loansData = response;
      } else if (response && response.data) {
        loansData = response.data;
      } else if (response && response.loans) {
        loansData = response.loans;
      }
      // normalize loans and compute daily_emi when missing
      const normalized = (loansData || []).map(l => {
        const amount = Number(l.requested_amount || l.amount || 0) || 0;
        const tenure = Number(l.tenure_days || l.tenureDays || l.tenure || 0) || 0;

        // Get loan type values (admin configured)
        const ltDaily = Number(l.loan_type_daily_emi || l.loanTypeDailyEmi || 0);
        const ltRecover = Number(l.loan_type_recover_amount || l.loanTypeRecoverAmount || l.recover_amount_effective || 0);
        const ltTenure = Number(l.loan_type_tenure_days || l.tenure_days || tenure || 0);
        
        // Get per-loan values
        const explicitEmi = Number(l.daily_emi || l.dailyEmi || l.emi || 0);
        const perLoanRecover = Number(l.recover_amount || l.recoverAmount || 0);

        // Priority 1: Use admin-configured loan type daily EMI (HIGHEST PRIORITY)
        let daily_emi = 0;
        if (ltDaily > 0) {
          daily_emi = ltDaily;
        } 
        // Priority 2: Calculate from loan type recover amount
        else if (ltRecover > 0 && ltTenure > 0) {
          daily_emi = Math.round(ltRecover / ltTenure);
        } 
        // Priority 3: Use explicit loan daily EMI
        else if (explicitEmi > 0) {
          daily_emi = explicitEmi;
        } 
        // Priority 4: Calculate from per-loan recover amount
        else if (perLoanRecover > 0 && tenure > 0) {
          daily_emi = Math.round(perLoanRecover / tenure);
        } 
        // Priority 5: Fallback to simple amount/tenure
        else if (amount > 0 && tenure > 0) {
          daily_emi = Math.round(amount / tenure);
        }

        // Determine effective recover amount for display
        // Priority: loan type recover -> per-loan recover -> computed from daily EMI
        let effectiveRecover = 0;
        if (ltRecover > 0) {
          effectiveRecover = ltRecover;
        } else if (perLoanRecover > 0) {
          effectiveRecover = perLoanRecover;
        } else if (daily_emi > 0 && tenure > 0) {
          effectiveRecover = Math.round(daily_emi * tenure);
        }

        console.log(`Loan ${l.id}: ltDaily=${ltDaily}, ltRecover=${ltRecover}, effectiveRecover=${effectiveRecover}, daily_emi=${daily_emi}`);

        return {
          ...l,
          requested_amount: amount,
          tenure_days: tenure,
          daily_emi: daily_emi,
          loan_type_daily_emi: ltDaily,
          loan_type_recover_amount: ltRecover,
          per_loan_recover_amount: perLoanRecover,
          recover_amount_effective: effectiveRecover
        };
      });

      setLoans(normalized);
      setSelectedLoan(normalized.length > 0 ? normalized[0] : null);
      const totalDue = (normalized || []).reduce((sum, loan) => {
        const dueAmount = Number(loan.due_amount || loan.dueAmount || 0) || 0;
        const dueCount = Number(loan.due_count || loan.dueCount || 0) || 0;
        const dailyEmi = Number(loan.daily_emi || 0) || 0;
        const effectiveDue = dueAmount > 0 ? dueAmount : (dueCount > 0 && dailyEmi > 0 ? dueCount * dailyEmi : dailyEmi);
        return sum + (Number.isFinite(effectiveDue) ? effectiveDue : 0);
      }, 0);
      setCurrentDue(totalDue);
      if (loansData.length === 0) {
        setError('No active loans found for this retailer');
      }
    } catch (err) {
      setError('Failed to load loans for retailer: ' + (err.message || 'Network error'));
      setLoans([]);
      setCurrentDue(0);
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchTodayCollections = async () => {
    try {
      const data = await collectionsAPI.getToday();
      setCollections(data);
    } catch (err) {
      console.error('Failed to load collections:', err);
    }
  };

  const handleRetailerChange = (e) => {
    const retailerId = e.target.value;
    setSelectedRetailer(retailerId);
    setFormData(prev => ({
      ...prev,
      retailerId,
      referenceNumber: '',
      notes: ''
    }));
    setSelectedLoan(null);
    if (retailerId) {
      fetchLoansForRetailer(retailerId);
    } else {
      setLoans([]);
      setCurrentDue(0);
      setSelectedLoan(null);
    }
  };

  const handleLoanChange = (e) => {
    const loanId = e.target.value;
    const loan = loans.find(l => String(l.id) === loanId);
    setSelectedLoan(loan);
    setFormData(prev => ({
      ...prev,
      referenceNumber: '',
      notes: ''
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.retailerId) {
      setError('Please select a retailer');
      return;
    }
    const emiAmount = Number(currentDue || 0);
    if (emiAmount <= 0) {
      setError('No due amount found for this retailer');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      const collectionData = {
        // include loanId when selected to satisfy backend validation explicitly
        retailerId: formData.retailerId,
        ...(selectedLoan && selectedLoan.id ? { loanId: selectedLoan.id } : {}),
        emiAmount,
        paymentMode: formData.paymentMode.toUpperCase(),
        collectionDate: new Date().toISOString().split('T')[0],
        referenceNumber: formData.referenceNumber?.trim() || null,
        notes: formData.notes?.trim() || `Collection: ₹${emiAmount}`
      };
      console.log('Recording collection:', collectionData);
      await collectionsAPI.record(collectionData);
      setSuccess(`Collection of ₹${emiAmount.toFixed(2)} recorded successfully!`);
      setFormData({
        retailerId: '',
        paymentMode: 'cash',
        referenceNumber: '',
        notes: ''
      });
      setSelectedLoan(null);
      setCurrentDue(0);
      fetchTodayCollections();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Submit error:', err);
      let backendMsg = err?.data?.message || err?.data?.error;
      setError(backendMsg || err.message || 'Failed to record collection');
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Record Collection</h1>
          <p>Loading active loans...</p>
        </div>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <div className="loader"></div>
          <p>Loading loans for collection...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Record Collection</h1>
        <p>Record loan collection and payment details from retailers</p>
      </div>

      <div className="form-container">
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Select Retailer *</label>
            <select
              name="retailerId"
              value={formData.retailerId}
              onChange={handleRetailerChange}
              required
              disabled={retailers.length === 0}
            >
              <option value="">
                {retailers.length === 0 ? 'No retailers available' : 'Select a Retailer'}
              </option>
              {retailers.map(retailer => (
                <option key={retailer.id} value={retailer.id}>
                  {retailer.shop_name} - {retailer.name} ({retailer.phone})
                </option>
              ))}
            </select>
            {retailers.length === 0 && (
              <small style={{ color: '#e74c3c' }}>
                Please register retailers first before recording collections.
              </small>
            )}
          </div>

          {/* Loan selector hidden — collections will be applied to retailer's oldest unpaid schedule */}
          
          <div className="form-group">
            <label>Current Due (₹)</label>
            <input
              type="text"
              name="currentDue"
              value={String(currentDue || 0)}
              disabled
              style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold', fontSize: '16px' }}
            />
            <small style={{ color: '#666', fontSize: '12px' }}>
              Sum of pending & missed EMIs up to today for this retailer.
            </small>
          </div>

          {selectedLoan && (
            <>
            <div className="grid grid-3" style={{ marginBottom: '20px' }}>
              <div className="dashboard-card">
                <div className="card-icon">🏪</div>
                <div className="card-content">
                  <div className="card-label">Retailer</div>
                  <div className="card-value">{selectedLoan.retailer_name || selectedLoan.retailerName || 'N/A'}</div>
                  <div className="card-subtitle">{selectedLoan.shop_name || 'Shop'}</div>
                </div>
              </div>
              <div className="dashboard-card">
                <div className="card-icon">📈</div>
                <div className="card-content">
                  <div className="card-label">Recover Amount</div>
                  <div className="card-value">₹{((selectedLoan.recover_amount_effective || selectedLoan.loan_type_recover_amount || selectedLoan.per_loan_recover_amount || 0)).toLocaleString()}</div>
                  <div className="card-subtitle">Used to compute EMI</div>
                </div>
              </div>
              <div className="dashboard-card">
                <div className="card-icon">📅</div>
                <div className="card-content">
                  <div className="card-label">Daily EMI</div>
                  <div className="card-value">₹{((selectedLoan.daily_emi || 0)).toLocaleString()}</div>
                  <div className="card-subtitle">{selectedLoan.tenure_days || 'N/A'} days tenure</div>
                </div>
              </div>
            </div>
            {/* recover card already shown in the grid */}
            </>
          )}

          {/* EMI amount per-loan removed; using retailer-level current due instead */}

          <div className="form-row">
            <div className="form-group">
              <label>Payment Mode *</label>
              <select 
                name="paymentMode" 
                value={formData.paymentMode} 
                onChange={handleInputChange}
                required
              >
                <option value="cash">💵 Cash</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Reference Number</label>
              <input
                type="text"
                name="referenceNumber"
                value={formData.referenceNumber || ''}
                onChange={handleInputChange}
                placeholder="Transaction/Receipt reference (optional)"
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                Transaction ID, receipt number, or check number
              </small>
            </div>
            <div className="form-group">
              <label>Collection Notes</label>
              <textarea
                name="notes"
                value={formData.notes || ''}
                onChange={handleInputChange}
                placeholder="Any additional notes about this collection..."
                rows="2"
              />
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="submit" 
              className="button button-primary" 
              disabled={loading || !formData.retailerId || Number(currentDue || 0) <= 0}
            >
              {loading ? 'Recording...' : 'Record Collection'}
            </button>
            <button
              type="reset"
              className="button button-secondary"
              onClick={() => {
                setFormData({
                  retailerId: '',
                  paymentMode: 'cash',
                  referenceNumber: '',
                  notes: ''
                });
                setSelectedLoan(null);
              }}
            >
              Clear Form
            </button>
          </div>
        </form>
      </div>

      {/* Today's Collections */}
      {collections.length > 0 && (
        <div style={{ marginTop: '30px' }}>
          <h2 style={{ marginBottom: '15px', color: '#333' }}>Today's Collections</h2>
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Loan ID</th>
                  <th>Retailer</th>
                  <th>Principal</th>
                  <th>Interest</th>
                  <th>Total</th>
                  <th>Mode</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {collections.map((col, idx) => (
                  <tr key={idx}>
                    <td>#{col.loanId}</td>
                    <td>{col.retailerName}</td>
                    <td>₹{col.principal?.toLocaleString() || 0}</td>
                    <td>₹{col.interest?.toLocaleString() || 0}</td>
                    <td><strong>₹{col.amount?.toLocaleString() || 0}</strong></td>
                    <td>{col.paymentMode}</td>
                    <td>{new Date(col.collectionDate).toLocaleTimeString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollectionRecording;
